<template>
  <div>
    <naj-box :title="locale.applicantRole">
      <v-row v-if="isNajizCitizen || isLawyer">
        <v-col cols="12" md="6" lg="3">
          <naj-input
            :label="locale.chooseRole"
            :type-id="formFieldType.radioButton"
            :value.sync="formModel.applicantRoleId"
            :items="applicantRoles"
            item-text="name"
            validates="required"
            item-value="id"
            :disabled="viewMode || isLawyer || isOrganizationUser"
            @change="applicantRoleChanged"
          ></naj-input>
        </v-col>
      </v-row>
      <v-row>
        <template>
          <original-applicant-form :view-mode="viewMode" :form-model="formModel.applicantInfo"></original-applicant-form>
        </template>
      </v-row>
    </naj-box>
    <representative-applicant-form
      v-if="isRepresentative || isOrganizationUser"
      :view-mode="viewMode"
      :form-model="formModel.originalPersonParty"
    ></representative-applicant-form>
    <naj-box
      v-if="isAttorneyApplicable"
      :title="locale.requestApplicant.attorneyDetails"
    >
      <v-row>
        <template>
          <attorney-form
            :form-model="formModel.representationInfoDetails"
            :attorney-social-id="formModel.applicantInfo.socialId"
            :related-social-id="formModel.originalPersonParty.socialId"
            :view-mode="viewMode"
          ></attorney-form>
        </template>
      </v-row>
    </naj-box>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { applicantRole } from '@/helpers/enum';
import OriginalApplicantForm from './original-applicant-form.vue';
import RepresentativeApplicantForm from './representative-applicant-form.vue';
import AttorneyForm from './attorney-form.vue';
import NajBox from '@/components/shared/naj-box.vue';
import { toHijriDate } from '@/helpers/utils';
import { partyTypeEnum } from '../../../../../helpers/enum';
import { userType, casePartyRoleEnum } from '@/helpers/enum';

const { mapActions: mapGlobalActions, mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapActions: mapLUTActions, mapState: mapLUTState } = createNamespacedHelpers('LookupsModule');

export default {
  name: 'RequestApplicantDetails',
  components: {
    OriginalApplicantForm,
    RepresentativeApplicantForm,
    AttorneyForm,
    NajBox,
  },
  inject: ['$validator'],
  props: {
    formModel: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    viewMode: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  data() {
    return {
      locale: this.local.caseRelatedRequest.reconsiderationFromNonCaseParty,
      applicantRole,
    };
  },
  computed: {
    ...mapLUTState(['countries', 'applicantRoles', 'genders']),
    ...mapGlobalState(['userInfo']),
    isOriginal() {
      return this.formModel.applicantRoleId == this.applicantRole.original;
    },
    isRepresentative() {
      return this.formModel.applicantRoleId == this.applicantRole.representative || this.formModel.applicantRoleId == this.applicantRole.organizationRepresentative;
    },
    isAttorneyApplicable() {
      return this.formModel.applicantRoleId == this.applicantRole.representative || this.isLawyer;
    },
    originalPersonParty() {
      return this.formModel.originalPersonParty || {};
    },
    applicantInfo() {
      return this.formModel.applicantInfo || {};
    },
    isIdentityVerified() {
      return this.formModel.originalPersonParty.isIdentityVerified;
    },
    isLawyer() {
      return this.userInfo && this.userInfo.userType === userType.najizLawyer;
    },
    isOrganizationUser() {
      return this.isFirmUser || this.isGovernmentalUser;
    },
    isNajizCitizen() {
      return this.userInfo && this.userInfo.userType === userType.najizCitizen;
    },
    isFirmUser() {
      return this.userInfo.isOrganizationUser && !this.isGovernmentalUser;
    },
    isGovernmentalUser() {
      return this.userInfo.loginType == 'governmental';
    },
  },
  mounted() {
    Promise.all([this.getCountries(), this.getUserInfo()]).then(() => {
      if (!this.viewMode) {
        if (!this.isOrganizationUser) {
          let applicantInfo = {};
          applicantInfo.socialIdType = this.userInfo.socialIdType;
          applicantInfo.socialId = this.userInfo.socialId;
          applicantInfo.fullName = this.userInfo.fullName;
          applicantInfo.firstName = this.userInfo.firstName;
          applicantInfo.fatherName = this.userInfo.fatherName;
          applicantInfo.grandFatherName = this.userInfo.grandFatherName;
          applicantInfo.familyName = this.userInfo.familyName;
          applicantInfo.dateOfBirth = this.userInfo.dateOfBirth;
          applicantInfo.dateOfBirthHijri = toHijriDate(this.userInfo.dateOfBirth);
          applicantInfo.gender = this.userInfo.gender;
          applicantInfo.nationalityCountryId = this.userInfo.nationalityCountryId;
          applicantInfo.name = this.userInfo.fullName;
          applicantInfo.nationalityName = this.countries.find((c) => c.id == this.userInfo.nationalityCountryId).name;

          if (this.isLawyer) this.$set(this.formModel, 'applicantRoleId', applicantRole.representative);
          else this.$set(this.formModel, 'applicantRoleId', applicantRole.original);

          this.$set(this.formModel, 'applicantInfo', applicantInfo);
          this.$set(this.formModel, 'applicantPartyType', partyTypeEnum.person);
        } else {
          this.$set(this.formModel, 'applicantRoleId', applicantRole.organizationRepresentative);
          let applicantInfo = {};
          const user = this.userInfo.impersonatedUser;
          applicantInfo.socialIdType = user.socialTypeId;
          applicantInfo.socialId = user.socialId;
          applicantInfo.fullName = user.givenName;
          applicantInfo.firstName = user.firstName;
          applicantInfo.fatherName = user.middleName;
          applicantInfo.grandFatherName = user.thirdName;
          applicantInfo.familyName = user.familyName;
          applicantInfo.dateOfBirth = user.dateOfBirth;
          applicantInfo.dateOfBirthHijri = toHijriDate(user.dateOfBirth);
          applicantInfo.gender = user.gender;
          applicantInfo.nationalityCountryId = user.nationalityId;
          applicantInfo.name = user.givenName;
          applicantInfo.nationalityName = this.countries.find((c) => c.id == user.nationalityId).name;
          applicantInfo.roleInCase = this.isGovernmentalUser ? casePartyRoleEnum.governmentalRepresentative.toString() : casePartyRoleEnum.companyRep.toString();
          this.$set(this.formModel, 'applicantInfo', applicantInfo);

          const organization = {
            firmName: this.userInfo.fullName,
            unifiedNumber: this.userInfo.nationalRecordNumber,
            commercialRegisterationNumber: this.userInfo.commercialRecordNumber,
            partyType: this.isGovernmentalUser ? partyTypeEnum.government : partyTypeEnum.firm,
            socialIdType: this.userInfo.socialIdType,
          };
          this.$set(this.formModel, 'originalPersonParty', organization);
          this.$set(this.formModel, 'applicantPartyType', this.isGovernmentalUser ? partyTypeEnum.government : partyTypeEnum.firm);

        }
      } else {
        if (this.formModel.applicantInfo) {
          this.$set(
            this.formModel.applicantInfo,
            'socialId',
            this.formModel.applicantInfo.socialId || this.formModel.applicantInfo.socialID,
          );
          this.$set(
            this.formModel.applicantInfo,
            'socialIdType',
            this.formModel.applicantInfo.socialIdType || this.formModel.applicantInfo.socialIDType,
          );
        }
      }
    });
  },
  methods: {
    ...mapLUTActions(['getCountries']),
    ...mapGlobalActions(['getUserInfo']),
    applicantRoleChanged() {
      if (this.isOriginal) {
        this.formModel.representationInfoDetails = null;
        this.formModel.originalPersonParty = null;
      } else {
        this.formModel.originalPersonParty = { casePartyRole: 10009, socialIdType: this.userInfo.socialIdType }; // reconsideredFromNonCaseParty
        this.formModel.representationInfoDetails = this.isLawyer ? { casePartyRole: 5 } : this.isOrganizationUser ? { casePartyRole: 10 } : { casePartyRole: 2 };
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
